'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var convertToJcamp = require('convert-to-jcamp');
var jcampconverter = require('jcampconverter');

function toJcamp(spectrum) {
  let jcamps = [];
  let weightVersusTemperature = spectrum.get('weightVersusTemperature');
  if (weightVersusTemperature) {
    jcamps.push(getJcamp(weightVersusTemperature));
  }
  let weightVersusTime = spectrum.get('weightVersusTime');
  if (weightVersusTime) {
    jcamps.push(getJcamp(weightVersusTime));
  }
  return jcamps.join('\n');
}

function getJcamp(spectrum) {
  let options = {
    xUnit: spectrum.xLabel,
    yUnit: spectrum.yLabel,
    title: spectrum.title,
    type: 'TGA',
    info: spectrum.meta,
  };
  return convertToJcamp.fromJSON({ x: spectrum.x, y: spectrum.y }, options);
}

function addJcamp(spectrum, jcamp) {
  const converted = jcampconverter.convert(jcamp, {
    xy: true,
    keepRecordsRegExp: /.*/,
    canonicDataLabels: false,
    dynamicTyping: true,
  });

  let currentSpectrum = converted.spectra[0];
  for (let oneSpectrum of converted.spectra) {
    let xLabel = currentSpectrum.xUnit;
    let yLabel = currentSpectrum.yUnit;

    let flavor = '';
    if (xLabel.match(/\[.*C\]/)) flavor = 'weightVersusTemperature';
    if (xLabel.match(/\[.*s\]/)) flavor = 'weightVersusTime';
    if (flavor) {
      spectrum.add(currentSpectrum.data[0].x, oneSpectrum.data[0].y, flavor, {
        xLabel,
        yLabel,
        title: currentSpectrum.title,
      });
    }
  }
}

/**
 * Class allowing manipulate a spectrum
 * @class spectrum
 * @param {object} [data={}] - object containing a spectrum
 * @param {Array} [data.x=[]] - voltage
 * @param {Array} [data.y=[]] - intensity
 */
class Spectrum {
  constructor() {
    this.flavors = {};
  }

  add(x, y, flavor = 'weightVersusTemperature', options = {}) {
    if (!flavor) {
      throw new Error('You need to specify the flavor of analysis');
    }
    this.flavors[flavor.toLowerCase()] = normalizeData(x, y, options);
  }

  addJcamp(jcamp) {
    addJcamp(this.flavors, jcamp);
  }

  get(flavor = 'weightVersusTemperature') {
    if (!flavor) {
      throw new Error('You need to specify the flavor of analysis');
    }
    flavor = flavor.toLowerCase();
    if (!this.flavors[flavor]) {
      throw new Error(`No spectrum for the flavor: ${flavor}`);
    }
    return this.flavors[flavor];
  }

  getXLabel(flavor = 'weightVersusTemperature') {
    return this.get(flavor).meta;
  }

  getYLabel() {
    return 'Intensity [A]';
  }
}

Spectrum.prototype.getData = function() {
  return { x: this.x, y: this.y };
};

Spectrum.prototype.toJcamp = function() {
  return toJcamp(this);
};

function normalizeData(x, y, options = {}) {
  const { meta = {}, xLabel = 'abc', yLabel = 'def', title = '' } = options;
  if (x && x.length > 1 && x[0] > x[1]) {
    x = x.reverse();
    y = y.reverse();
  } else {
    x = x || [];
    y = y || [];
  }
  return {
    x,
    y,
    xLabel,
    yLabel,
    title,
    meta,
  };
}

function parsePerkinElmer(text) {
  let lines = text.split(/[\r\n]+/);
  let result = { meta: {}, data: { time: [], weight: [], temperature: [] } };
  let section = '';
  let inMethodSteps = false;
  for (let line of lines) {
    if (inMethodSteps) {
      if (line.startsWith('1) TGA')) {
        inMethodSteps = false;
      } else {
        if (!result.meta['Method Steps']) result.meta['Method Steps'] = '';
        result.meta['Method Steps'] += `${line.replace(/\t/g, '  ')}\n`;
      }
    } else if (line.match(/^[a-zA-Z -]+$/)) {
      section = trim(line);
    } else if (line.match(/.*:.*/)) {
      let position = line.indexOf(':');
      let description = line.substring(0, position);
      let value = trim(line.substring(position + 1));
      result.meta[(section ? `${section}_` : '') + description] = value;
    } else if (line.match(/^[0-9\t .]+$/)) {
      let fields = line.replace(/^\t/, '').split('\t');
      result.data.time.push(Number(fields[0]));
      result.data.weight.push(Number(fields[1]));
      result.data.temperature.push(Number(fields[4]));
    }
    if (line.startsWith('Method Steps:')) {
      inMethodSteps = true;
    }
  }
  return result;
}

function trim(string) {
  return string.replace(/^[ \t]*(.*?)[ \t]*$/, '$1');
}

/**
 * Creates a new Chromatogram element based in a JCAMP string
 * @param {string} text - String containing the JCAMP data
 * @return {Spectrum} - New class element with the given data
 */
function fromPerkinElmer(text) {
  let spectrum = new Spectrum();
  let result = parsePerkinElmer(text);

  spectrum.add(
    result.data.temperature,
    result.data.weight,
    'weightVersusTemperature',
    {
      xLabel: 'Temperature [°C]',
      yLabel: 'Weight [mg]',
      title: result.meta['Sample ID'],
      meta: result.meta,
    },
  );
  spectrum.add(result.data.time, result.data.weight, 'weightVersusTime', {
    xLabel: 'Time [s]',
    yLabel: 'Weight [mg]',
    title: result.meta['Sample ID'],
    meta: result.meta,
  });
  return spectrum;
}

/**
 * Creates a new Chromatogram element based in a JCAMP string
 * @param {string} jcamp - String containing the JCAMP data
 * @return {Spectrum} - New class element with the given data
 */
function fromJcamp(jcamp) {
  let tree = jcampconverter.createTree(jcamp);
  let spectrum = new Spectrum();
  for (let item of tree) {
    addJcamp(spectrum, item.jcamp);
  }

  return spectrum;
}

exports.Spectrum = Spectrum;
exports.fromJcamp = fromJcamp;
exports.fromPerkinElmer = fromPerkinElmer;
